import React, { FC, useCallback, useEffect, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { Printer } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { analyticsService } from '../../analytics/AnalyticsService';
import { useSession } from '../../hooks/useSession';
import { useColors } from '../../hooks/useColors';
import { useSettingsSync } from './SettingsSyncContext';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import { KdsPrinter, usePrinters } from '../../hooks/usePrinters';
import SettingsItem from '../../component/SettingsItem/SettingsItem';
import SectionHeading from './SectionHeading';
import SettingsScreenStyles from './Settings.styles';

const WorkflowSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const { translate } = useTranslation();
  const { session, setSession } = useSession();
  const { colors } = useColors();
  const { loading } = useSettingsSync();
  const { loading: printersLoading, printers } = usePrinters();

  // The sync context only tracks the org-settings sync; the printer list has a
  // fetch of its own. The printer rows have to reflect both, or an in-flight
  // printer query reads as "no printers configured".
  const printerRowsLoading = loading || printersLoading;
  const { updateKitchenDisplay } = useKitchenDisplay({
    storeId: session?.currentStore?.id,
  });

  const [showPrinters, setShowPrinters] = useState<boolean>(false);

  const [isAutoPrint, setIsAutoPrint] = useState<boolean>(
    session?.kitchenDisplay?.autoPrintOnCompletion || false,
  );
  const [isSingleItemPrinting, setSingleItemPrinting] = useState<boolean>(
    session?.kitchenDisplay?.singleItemPrinting || false,
  );
  const [isTwoLevel, setIsTwoLevel] = useState<boolean>(
    session?.rebumpToggle || false,
  );
  const [isSeatManagementEnabled, setIsSeatManagemenetEnabled] =
    useState<boolean>(session?.enableSeatManagement || false);
  const [groupItemsByStatus, setGroupItemsByStatus] = useState<boolean>(
    session?.kitchenDisplay?.groupItemsByStatus ?? true,
  );
  const [isCourseCallEnabled, setIsCourseCallEnabled] = useState<boolean>(
    session?.kitchenDisplay?.isCourseCallEnabled || false,
  );
  const [isDocketPrintEnabled, setIsDocketPrintEnabled] =
    useState<boolean>(false);

  // Latches on once a printer is assigned and never resets, matching the
  // screen this replaced. `|| null` rather than `?? null` is deliberate: any
  // falsy printer counts as unassigned.
  useEffect(() => {
    if (!isDocketPrintEnabled && !loading) {
      const printer = session.kitchenDisplay?.printer || null;
      setIsDocketPrintEnabled(printer !== null);
    }
  }, [isDocketPrintEnabled, loading, session.kitchenDisplay?.printer]);

  const onSelectPrinter = useCallback(
    (printer: KdsPrinter) => {
      if (printer.id === session?.kitchenDisplay?.printer?.id) return;
      analyticsService.capture('Changed KDS Printer');
      updateKitchenDisplay({
        id: session?.kitchenDisplay?.id || '',
        printer: printer.id,
      });
      setSession({
        kitchenDisplay: {
          ...session.kitchenDisplay,
          // The session models this as a full `Printer`, but the KDS query
          // deliberately selects only the fields the printing path needs. The
          // cast records that gap rather than widening the query.
          printer: printer as Printer,
        },
      });
    },
    [session.kitchenDisplay, setSession, updateKitchenDisplay],
  );

  const onToggleAutoPrint = () => {
    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      autoPrintOnCompletion: !isAutoPrint,
    });
    setIsAutoPrint(!isAutoPrint);
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        autoPrintOnCompletion: !isAutoPrint,
      },
    });
  };

  const onToggleSingleItemPrinting = () => {
    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      singleItemPrinting: !isSingleItemPrinting,
    });
    setSingleItemPrinting(prevState => !prevState);
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        singleItemPrinting: !isSingleItemPrinting,
      },
    });
  };

  const onToggleTwoLevel = () => {
    analyticsService.capture('Toggled 2-Level Fulfilment');
    setIsTwoLevel(!isTwoLevel);
    setSession({ rebumpToggle: !isTwoLevel });
  };

  const onToggleEnableSeatManagement = () => {
    setIsSeatManagemenetEnabled(!isSeatManagementEnabled);
    setSession({ enableSeatManagement: !isSeatManagementEnabled });
  };

  const onToggleGroupItemsByStatus = () => {
    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      groupItemsByStatus: !groupItemsByStatus,
    });
    setGroupItemsByStatus(!groupItemsByStatus);
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        groupItemsByStatus: !groupItemsByStatus,
      },
    });
  };

  const onToggleCourseCall = () => {
    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      isCourseCallEnabled: !isCourseCallEnabled,
    });
    setIsCourseCallEnabled(!isCourseCallEnabled);
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        isCourseCallEnabled: !isCourseCallEnabled,
      },
    });
  };

  return (
    <View style={styles.panel} ph-label="settings-workflow">
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-printing"
          title={translate('kitchenDisplay.printing')}
        />
        <SettingsItem
          type="parent"
          testID="select-printer"
          loading={printerRowsLoading}
          title={translate('kitchenDisplay.printer')}
          checked={showPrinters}
          subtitle={
            session?.kitchenDisplay?.printer?.name ||
            translate('kitchenDisplay.noPrinterSelected')
          }
          onChange={() => setShowPrinters(!showPrinters)}
        />
        {showPrinters ? (
          <View style={styles.subSection}>
            {!printers.length && printersLoading ? (
              <ActivityIndicator
                testID="printers-loading"
                color={colors.text}
              />
            ) : null}
            {printers.map(printer => (
              <SettingsItem
                key={printer.id}
                type="select"
                testID={`printer-${printer.id}`}
                loading={printerRowsLoading}
                title={printer.name}
                checked={printer.id === session?.kitchenDisplay?.printer?.id}
                onChange={() => onSelectPrinter(printer)}
              />
            ))}
          </View>
        ) : null}
        {isDocketPrintEnabled && (
          <>
            <SettingsItem
              type="switch"
              testID="toggle-autoPrint"
              loading={loading}
              title={translate('kitchenDisplay.autoPrintDocket')}
              onChange={onToggleAutoPrint}
              checked={isAutoPrint}
            />
            <SettingsItem
              type="switch"
              testID="toggle-singleItemPrinting"
              loading={loading}
              title={translate('kitchenDisplay.singleItemPrinting')}
              onChange={onToggleSingleItemPrinting}
              checked={isSingleItemPrinting}
            />
          </>
        )}
      </View>
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-ticketBehaviour"
          title={translate('kitchenDisplay.ticketBehaviour')}
        />
        <SettingsItem
          type="switch"
          testID="toggle-twoLevel"
          loading={loading}
          title={translate('kitchenDisplay.rebumpToggle')}
          onChange={onToggleTwoLevel}
          checked={isTwoLevel}
        />
        <SettingsItem
          type="switch"
          testID="toggle-seatNumbers"
          loading={loading}
          title={translate('kitchenDisplay.showSeatNumbers')}
          onChange={onToggleEnableSeatManagement}
          checked={isSeatManagementEnabled}
        />
        <SettingsItem
          type="switch"
          testID="toggle-groupItemsByStatus"
          loading={loading}
          title={translate('kitchenDisplay.groupItemsByStatus')}
          onChange={onToggleGroupItemsByStatus}
          checked={groupItemsByStatus}
        />
        <SettingsItem
          type="switch"
          testID="toggle-courseCall"
          loading={loading}
          title={translate('kitchenDisplay.useCallCourse')}
          onChange={onToggleCourseCall}
          checked={isCourseCallEnabled}
        />
      </View>
    </View>
  );
};

export default WorkflowSettings;
